<template>
    <v-container v-if="isLoading" fluid class="justify-center fill-height">
        <v-progress-circular :size="50" indeterminate color="primary"></v-progress-circular>
    </v-container>
    <div v-else>
        <v-toolbar class="elevation-1">
            <v-toolbar-title>{{ title }}</v-toolbar-title>
            <div class="flex-grow-1"></div>
            <template>
                <v-btn
                        color="primary"
                        class="ma-0"
                        min-width="0"
                        @click="submit"
                >
                    <v-icon>done</v-icon>
                </v-btn>
            </template>
        </v-toolbar>
        <v-form autocomplete="off"
                ref="form"
                v-model="valid"
        >
            <FormCard>
                <template v-slot:form>
                    <v-row dense>
                        <!-- Company -->
                        <v-col cols="12" sm="6" md="3" lg="3" xl="3">
                            <v-select class="custom_textfield"
                                      :items="companies"
                                      v-model="invoice.company_id"
                                      :label="$t('general.company')"
                                      type="select"
                                      item-text="name"
                                      item-value="id"
                                      outlined
                                      :rules="field_required_rule"
                            ></v-select>
                        </v-col>

                        <!-- Bank Account -->
                        <v-col cols="12" sm="6" md="3" lg="3" xl="3">
                            <v-select class="custom_textfield"
                                      :items="filtered_bank_accounts"
                                      v-model="invoice.bank_account_id"
                                      :label="$t('bank_accounts.singular')"
                                      type="select"
                                      item-text="name"
                                      item-value="id"
                                      outlined
                                      :rules="field_required_rule"
                            ></v-select>
                        </v-col>

                        <!-- Client -->
                        <v-col cols="12" sm="6" md="3" lg="3" xl="3">
                            <v-select class="custom_textfield"
                                      :items="clients"
                                      v-model="invoice.client_id"
                                      :label="$t('general.client')"
                                      type="select"
                                      item-text="name"
                                      item-value="id"
                                      outlined
                                      :rules="field_required_rule"
                            ></v-select>
                        </v-col>

                        <!-- Date -->
                        <v-col cols="12" sm="6" md="3" lg="3" xl="3">
                            <v-menu v-model="date_picker"
                                    :close-on-content-click="false"
                                    :nudge-right="40"
                                    transition="scale-transition"
                                    offset-y
                                    min-width="290px"
                            >
                                <template v-slot:activator="{ on }">
                                    <v-text-field class="custom_textfield"
                                                  v-model="formattedDate"
                                                  :label="$t('general.date')"
                                                  append-icon="event"
                                                  readonly
                                                  v-on="on"
                                                  outlined
                                                  :rules="field_required_rule"
                                    ></v-text-field>
                                </template>
                                <v-date-picker v-model="invoice.date"
                                               @input="date_picker = false"
                                ></v-date-picker>
                            </v-menu>
                        </v-col>

                        <!-- Create button -->
                        <v-col cols="12" sm="6" md="3" lg="3" xl="3">
                            <v-btn color="primary"
                                   @click="addService"
                                   v-html="$t('invoices.add_service')"
                            ></v-btn>
                        </v-col>

                    </v-row>
                </template>
            </FormCard>

            <FormCard v-for="(value, index) in services" :key="index">
                <template v-slot:form>
                    <v-row dense>
                        <!-- Client -->
                        <v-col cols="12" sm="8" md="6" lg="6" xl="6">
                            <v-text-field
                                    class="custom_textfield"
                                    v-model="value.description"
                                    :label="$t('general.description')"
                                    outlined
                                    :rules="field_required_rule"
                            ></v-text-field>
                        </v-col>
                        <!-- Project -->
                        <v-col cols="12" sm="3" md="3" lg="2" xl="1">
                            <v-text-field
                                    class="custom_textfield"
                                    v-model="value.price"
                                    :label="$t('general.price')"
                                    outlined
                                    type="number"
                                    min="0"
                                    :rules="field_required_rule"
                            ></v-text-field>
                        </v-col>
                        <!-- Close button -->
                        <v-col cols="12" sm="1" md="3" lg="4" xl="5" v-if="index !== services.length - 1"
                               class="d-flex justify-end justify-sm-end justify-md-end">
                            <v-btn icon
                                   class="top_close_btn"
                                   @click="deleteService(index, $event)"
                            >
                                <v-icon>close</v-icon>
                            </v-btn>
                        </v-col>
                    </v-row>
                </template>
            </FormCard>

            <FormCard>
                <template v-slot:form>
                    <v-row dense>

                        <!-- Vat -->
                        <v-col cols="12" offset-sm="5" sm="3" offset-md="3" md="3" offset-lg="4" lg="2" offset-xl="5"
                               xl="1">
                            <v-select class="custom_textfield"
                                      :items="vats"
                                      v-model="invoice.vat"
                                      :label="$t('general.vat')"
                                      type="select"
                                      item-text="value"
                                      item-value="id"
                                      outlined
                                      :rules="field_required_rule"
                            ></v-select>
                        </v-col>

                        <!-- Total Price -->
                        <v-col cols="12" sm="3" md="3" lg="2" xl="1">
                            <v-text-field
                                    class="custom_textfield"
                                    v-model="total_price"
                                    :label="$t('general.total_price')"
                                    outlined
                                    readonly
                                    type="number"
                                    min="0"
                                    :rules="field_required_rule"
                            ></v-text-field>
                        </v-col>

                    </v-row>
                </template>
            </FormCard>

        </v-form>
    </div>
</template>

<script>
    import FormCard from '../components/FormCard.vue'

    export default {
        components: {
            FormCard
        },
        props: {
            title: {
                type: String
            },
            item_id: {
                default: null
            }
        },
        data() {
            return {
                isLoading: false,
                url: '/api/invoices',

                // data
                companies: [],
                clients: [],
                bank_accounts: [],
                filtered_bank_accounts: [],
                vats: [],

                // item
                date_picker: false,
                invoice: {
                    company_id: null,
                    client_id: null,
                    services_price: null,
                    vat: null,
                    total_price: null,
                    bank_account_id: null,
                    date: new Date().toISOString().substr(0, 10)
                },
                service_obj: {
                    description: null,
                    price: null
                },
                services: [],

                // Validation
                valid: true,
                field_required_rule: [
                    v => !!v || `${this.$t('validation.required')}`
                ],
            }
        },
        computed: {
            company() {
                return this.invoice.company_id;
            },
            total_price() {
                // not selected vat
                if (!this.invoice.vat) {
                    return this.invoice.services_price;
                }

                // found selected vat
                let vat = this.vats.find(obj => obj.id === this.invoice.vat);

                // calculate price
                let price = (vat.value) ? (this.invoice.services_price * vat.value) / 100 + this.invoice.services_price
                    : this.invoice.services_price;

                this.invoice.total_price = price;
                return price;
            },
            formattedDate() {
                return this.invoice.date ? this.moment(this.invoice.date).format('DD/MM/YYYY') : ''
            },
        },
        async mounted() {
            this.isLoading = true;

            // create item
            if (!this.item_id) {
                this.services.push(Object.assign({}, this.service_obj));
            }

            await this.getLookUpData();

            // edit item
            if (this.item_id) {
                await this.fetchItem();
            }

            this.isLoading = false;
        },
        watch: {
            // clear account selection and filter accounts based on selected company
            company(val) {
                this.invoice.bank_account_id = null;
                this.filtered_bank_accounts = this.bank_accounts.filter(obj => obj.company_id === val);
            },
            services: {
                handler(val) {
                    this.invoice.services_price = 0;

                    val.forEach(obj => {
                        if (obj.price) {
                            this.invoice.services_price += parseInt(obj.price);
                        }
                    });
                },
                deep: true
            }
        },
        methods: {
            // get all look up data
            async getLookUpData() {
                this.companies = (await this.$store.dispatch('fetchData', {url: '/api/companies'}))[0];

                this.bank_accounts = (await this.$store.dispatch('fetchData', {
                    url: '/api/bank-accounts/all', params: {conditions: {is_active: 1}}
                }))[0];

                this.filtered_bank_accounts = [...this.bank_accounts];

                this.clients = (await this.$store.dispatch('fetchData', {
                    url: '/api/clients/all', params: {projects_conditions: {is_active: 1}}
                }))[0];

                this.vats = (await this.$store.dispatch('fetchData', {url: '/api/vats/all'}))[0];
            },
            // Get work hours from API
            async fetchItem() {
                let response = await this.$store.dispatch('fetchData', {url: `${this.url}/${this.item_id}`});

                if (response.data) {

                    for (let prop in this.invoice) {
                        if (prop !== 'bank_account_id') {
                            this.invoice[prop] = response.data[prop];
                        }
                    }

                    // set after watcher apply company's bank accounts
                    setTimeout(() => this.invoice.bank_account_id = response.data.bank_account_id, 20);

                    response.data.services.forEach(obj => {
                        this.services.push({description: obj.description, price: obj.price});
                    });
                }
            },

            // add service to invoice
            addService() {
                this.services.unshift(Object.assign({}, this.service_obj));
                this.$refs.form.resetValidation();
            },
            // delete service from invoice
            deleteService(index) {
                this.services.splice(index, 1);
            },
            // send API request to create or update Invoice
            async submit() {
                if (this.$refs.form.validate()) {

                    let response = null;
                    let data = {invoice: this.invoice, services: this.services};

                    // If work hour id is NN and current route is not repeat route, update
                    if (this.item_id && this.$route.name !== 'repeat_invoices') {

                        // Update data
                        response = await this.$store.dispatch('sendData', {
                            method: 'patch', url: `${this.url}/${this.item_id}`, data: data
                        });

                        // Else, create new entry
                    } else {

                        // Post data
                        response = await this.$store.dispatch('sendData', {
                            method: 'post', url: this.url, data: data
                        });
                    }

                    // After successful action, redirect to view work hours
                    if (response && response.status === 200) {
                        this.$router.push('/invoices/view')
                    }
                }
            }
        }
    }
</script>